import React, { useState, useEffect, useCallback } from 'react';
import useArticleStore from './stores/useArticleStore';
import useAutosave from './hooks/useAutosave';
import useCsrfToken from './hooks/useCsrfToken';
import { TranslationProvider, useTranslations } from './contexts/TranslationContext';
import { completeOnboarding } from './api';
import AutosaveIndicator from '../shared/AutosaveIndicator';
import ArticleTitle from './ArticleTitle';
import FormBody from './FormBody';
import FormSidebar from './FormSidebar';
import PublishPanel from './PublishPanel';
import OnboardingTour from './OnboardingTour';
import ProgressBar from './ProgressBar';

function ArticleFormInner({ mode, onboardingCompleted, hospitals, article, auditEntries }) {
  var t = useTranslations();
  var csrfToken = useCsrfToken();
  var setArticle = useArticleStore(function(state) { return state.setArticle; });
  var resetStore = useArticleStore(function(state) { return state.resetStore; });
  var publishErrors = useArticleStore(function(state) { return state.publishErrors; });
  var saveStatus = useArticleStore(function(state) { return state.saveStatus; });
  var lastSavedAt = useArticleStore(function(state) { return state.lastSavedAt; });
  var autosave = useAutosave();

  var onboardingState = useState(!onboardingCompleted);
  var showOnboarding = onboardingState[0];
  var setShowOnboarding = onboardingState[1];

  // Reset store on mount to clear stale Turbolinks state
  useEffect(function() {
    resetStore();
    if (mode === 'edit' && article) {
      setArticle(article);
    }
    // Auto-show onboarding on first visit (cookie not set)
    setShowOnboarding(!onboardingCompleted);
  }, []);

  var handleOnboardingComplete = useCallback(function() {
    setShowOnboarding(false);
    completeOnboarding(csrfToken).catch(function() {});
  }, [csrfToken]);

  var pageTitle = mode === 'edit' ? t.edit_title : t.create_title;

  return (
    <div className="article-form-new">
      <div className="card mb-3 article-form-new__header-card">
        <div className="card-body">
          <div className="article-form-new__header">
            <div className="article-form-new__header-left">
              <a className="article-form-new__back" href="/articles" data-turbolinks="false">
                <i className="zmdi zmdi-arrow-left" />
              </a>
              <div className="article-form-new__title-row">
                <h4 className="article-form-new__title">{pageTitle}</h4>
                <span data-tour="autosave">
                  <AutosaveIndicator saveStatus={saveStatus} lastSavedAt={lastSavedAt} onRetry={autosave.saveNow} translations={t} />
                </span>
              </div>
              {publishErrors && Object.keys(publishErrors).length > 0 && (
                <div className="article-form-new__header-errors">
                  {Object.entries(publishErrors).map(function(entry) {
                    var field = entry[0];
                    var messages = entry[1];
                    return messages.map(function(msg, i) {
                      return (
                        <span key={field + '-' + i} className="article-form-new__header-error">
                          <i className="zmdi zmdi-alert-circle-o" />
                          {field !== 'base' ? field.replace('_', ' ') + ': ' : ''}{msg}
                        </span>
                      );
                    });
                  })}
                </div>
              )}
            </div>
            <div className="article-form-new__header-right" data-tour="publish_panel">
              <PublishPanel saveNow={autosave.saveNow} compact />
              <button
                type="button"
                className="article-form-new__tour-btn"
                onClick={function() { setShowOnboarding(true); }}
                title={t.guided_tour}
              >
                <i className="zmdi zmdi-help-outline" />
              </button>
            </div>
          </div>
          <ProgressBar />
        </div>
      </div>

      <div className="row">
        <div className="col-lg-8">
          <div className="card">
            <div className="card-body">
              <ArticleTitle scheduleAutosave={autosave.scheduleAutosave} />
              <FormBody
                scheduleAutosave={autosave.scheduleAutosave}
                saveNow={autosave.saveNow}
                csrfToken={csrfToken}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-4 article-form-new__sidebar-col">
          <div className="form-sidebar">
            <FormSidebar
              hospitals={hospitals}
              scheduleAutosave={autosave.scheduleAutosave}
              auditEntries={mode === 'edit' ? auditEntries : null}
            />
          </div>
        </div>
      </div>

      {showOnboarding && (
        <OnboardingTour
          isOpen={showOnboarding}
          onComplete={handleOnboardingComplete}
        />
      )}
    </div>
  );
}

function ArticleFormNew(props) {
  return (
    <TranslationProvider translations={props.translations}>
      <ArticleFormInner
        mode={props.mode}
        onboardingCompleted={props.onboardingCompleted}
        hospitals={props.hospitals}
        article={props.article}
        auditEntries={props.auditEntries}
      />
    </TranslationProvider>
  );
}

export default ArticleFormNew;
